import {
  queryOptions,
  useQuery,
  type UseQueryResult,
} from "@tanstack/react-query";

import type { ApiClient } from "@/api/client";
import { useApiClient } from "@/api/client-context";
import { qk } from "@/api/query-keys";

/** 起票の選択肢として見せる案件種別（＝プロセスモデル）。 */
export type RunnableModel = {
  readonly processModelId: string;
  readonly displayName: string;
  readonly description: string | null;
  /** 所属グループの階層（表示名つき）。階層表示と検索に使う。 */
  readonly groupPath: readonly {
    readonly id: string;
    readonly displayName: string;
  }[];
};

type SpiffProcessModelRow = {
  readonly id: string;
  readonly display_name: string;
  readonly description?: string | null;
  readonly is_executable?: boolean;
  readonly parent_groups?: readonly {
    readonly id?: string | null;
    readonly display_name?: string | null;
  }[];
  readonly display_order?: number | null;
};

type SpiffProcessModelListResponse = {
  readonly results?: readonly SpiffProcessModelRow[];
};

/**
 * 起票できる案件種別の一覧。
 *
 * `filter_runnable_by_user` を付けるのは、起票権限（PM の start）がないモデルを
 * 選択肢に出さないため。権限は backend が判定するので UI 側で持たない。
 */
export function runnableModelsQueryOptions(client: ApiClient) {
  const fetchModels = async (): Promise<readonly RunnableModel[]> => {
    const response = await client.get<SpiffProcessModelListResponse>(
      "/process-models",
      {
        query: {
          per_page: 100,
          page: 1,
          recursive: true,
          filter_runnable_by_user: true,
          // グループの表示名を取る（階層表示・グループ名での検索に使う）
          include_parent_groups: true,
        },
      },
    );

    return (
      (response.results ?? [])
        .filter((row) => row.is_executable !== false)
        .map((row) => ({
          groupPath: toGroupPath(row.parent_groups),
          processModelId: row.id,
          displayName: row.display_name,
          description:
            row.description === undefined || row.description === ""
              ? null
              : row.description,
          sortKey: row.display_order ?? 0,
        }))
        // 同順位は id 順。表示名でのソートはロケール依存で不安定なため使わない。
        // 同値は 0（比較関数の契約。1 を返すと同一 id が混ざったとき並びが揺れる）。
        .sort(
          (a, b) =>
            a.sortKey - b.sortKey ||
            compare(a.processModelId, b.processModelId),
        )
        .map(({ sortKey: _sortKey, ...model }) => model)
    );
  };

  return queryOptions({
    queryKey: qk.processModels.runnableList(),
    queryFn: fetchModels,
    // 案件種別はほとんど変わらないので長めに保つ
    staleTime: 5 * 60_000,
  });
}

export function useRunnableModels(): UseQueryResult<readonly RunnableModel[]> {
  return useQuery(runnableModelsQueryOptions(useApiClient()));
}

/** parent_groups を階層（表示名つき）に整える。表示名が無ければ id を使う。 */
function toGroupPath(
  parents: SpiffProcessModelRow["parent_groups"],
): readonly { readonly id: string; readonly displayName: string }[] {
  return (parents ?? [])
    .filter((parent) => typeof parent.id === "string" && parent.id !== "")
    .map((parent) => ({
      id: parent.id as string,
      displayName:
        parent.display_name === undefined ||
        parent.display_name === null ||
        parent.display_name === ""
          ? (parent.id as string)
          : parent.display_name,
    }));
}

/** 文字列の順序比較（同値は 0）。 */
function compare(a: string, b: string): number {
  if (a === b) return 0;
  return a < b ? -1 : 1;
}
